import React from 'react';
import { Link } from 'react-router-dom';
import { Check, Users, Award, Target, ArrowRight, Sparkles, TrendingUp, Zap, Shield, Clock, Globe, Heart } from 'lucide-react';
import { companyInfo } from '../mock';
import SEO from '../components/SEO';

const About = ({ onCtaClick }) => {
  return (
    <div className="bg-white text-gray-900 min-h-screen">
      <SEO 
        title="About Us - IlinkTrading | Digital Agency Experts"
        description="Learn about IlinkTrading's mission to transform businesses through innovative technology solutions. Meet our team of expert developers and designers."
        keywords="about us, digital agency team, tech experts, web developers, blockchain experts, company mission"
      />
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 px-4 lg:px-8 bg-gradient-to-br from-purple-50 via-pink-50 to-orange-50 overflow-hidden">
        <div className="absolute top-20 right-10 w-72 h-72 bg-purple-200 rounded-full mix-blend-multiply filter blur-xl opacity-30 animate-blob" />
        <div className="absolute top-40 left-10 w-72 h-72 bg-pink-200 rounded-full mix-blend-multiply filter blur-xl opacity-30 animate-blob animation-delay-2000" />
        
        <div className="max-w-7xl mx-auto text-center relative z-10">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-white rounded-full shadow-md mb-6">
            <Sparkles className="text-purple-600" size={18} />
            <span className="text-sm font-semibold text-gray-700">About Us</span>
          </div>
          <h1 className="text-5xl md:text-6xl font-bold mb-6">
            <span className="bg-gradient-to-r from-purple-600 via-pink-600 to-orange-600 bg-clip-text text-transparent">
              Advancing Innovation
            </span>
            <br />
            <span className="text-gray-900">Through Technology</span>
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Empowering businesses with cutting-edge solutions that drive sustainable growth and create exceptional experiences
          </p>
        </div>
      </section>

      {/* Who We Are */}
      <section className="py-24 px-4 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div className="relative order-2 lg:order-1">
              <div className="absolute -inset-4 bg-gradient-to-r from-purple-500 to-pink-600 rounded-3xl opacity-20 blur-2xl" />
              <div className="relative rounded-3xl overflow-hidden shadow-2xl">
                <img
                  src="https://images.unsplash.com/photo-1519389950473-47ba0277781c?w=800&q=85"
                  alt="Our Team"
                  className="w-full h-full object-cover"
                />
                <div className="absolute bottom-6 left-6 right-6 bg-white/90 backdrop-blur-sm rounded-2xl p-6 shadow-xl">
                  <div className="grid grid-cols-3 gap-4 text-center">
                    <div>
                      <div className="text-2xl font-bold text-purple-600">{companyInfo.projects}</div>
                      <div className="text-xs text-gray-600">Projects</div>
                    </div>
                    <div>
                      <div className="text-2xl font-bold text-pink-600">{companyInfo.clients}</div>
                      <div className="text-xs text-gray-600">Clients</div>
                    </div>
                    <div>
                      <div className="text-2xl font-bold text-orange-600">{companyInfo.teamSize}</div>
                      <div className="text-xs text-gray-600">Team</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="order-1 lg:order-2 space-y-6">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-purple-50 rounded-full">
                <Users className="text-purple-600" size={18} />
                <span className="text-sm font-semibold text-purple-600">Our Story</span>
              </div>
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 leading-tight">
                Transforming Ideas Into
                <span className="bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent"> Digital Reality</span>
              </h2>
              <p className="text-lg text-gray-600 leading-relaxed">
                We specialize in IT consulting and software development, empowering businesses to harness 
                technology for sustainable growth. Our expertise spans industries, enabling companies to build 
                innovative, scalable, and high-performing digital solutions.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="flex items-start gap-4 p-4 bg-gradient-to-br from-purple-50 to-pink-50 rounded-xl">
                  <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-pink-500 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Check className="text-white" size={20} strokeWidth={3} />
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-1">Custom Solutions</h4>
                    <p className="text-sm text-gray-600">Tailored to your needs</p>
                  </div>
                </div>
                <div className="flex items-start gap-4 p-4 bg-gradient-to-br from-orange-50 to-red-50 rounded-xl">
                  <div className="w-10 h-10 bg-gradient-to-br from-orange-500 to-red-500 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Check className="text-white" size={20} strokeWidth={3} />
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-1">Expert Team</h4>
                    <p className="text-sm text-gray-600">Industry professionals</p>
                  </div>
                </div>
              </div>
              <button
                onClick={() => onCtaClick && onCtaClick()}
                className="inline-flex items-center gap-2 bg-gradient-to-r from-purple-600 to-pink-600 text-white px-8 py-4 rounded-xl text-base font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-1 transition-all duration-300"
              >
                Get a Free Quote
                <ArrowRight size={20} />
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-24 px-4 lg:px-8 bg-gradient-to-br from-gray-50 to-purple-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Why <span className="bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">Partner With Us</span>
            </h2>
            <p className="text-xl text-gray-600">What sets us apart in the digital landscape</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="group bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 border-2 border-purple-100">
              <div className="w-16 h-16 bg-gradient-to-br from-purple-500 to-pink-500 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                <Users className="text-white" size={32} />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-3">Expert Team</h3>
              <p className="text-gray-600 leading-relaxed">
                {companyInfo.teamSize} experienced professionals dedicated to delivering excellence and innovation in every project
              </p>
            </div>

            <div className="group bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 border-2 border-blue-100">
              <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                <Award className="text-white" size={32} />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-3">Proven Track Record</h3>
              <p className="text-gray-600 leading-relaxed">
                {companyInfo.projects} completed projects with {companyInfo.clients} satisfied clients across various industries
              </p>
            </div>

            <div className="group bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 border-2 border-orange-100">
              <div className="w-16 h-16 bg-gradient-to-br from-orange-500 to-red-500 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                <Target className="text-white" size={32} />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-3">Results Driven</h3>
              <p className="text-gray-600 leading-relaxed">
                Focused on delivering measurable outcomes that drive business growth and exceed expectations
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Mission Section */}
      <section className="py-24 px-4 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2 space-y-6">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-blue-50 rounded-full">
                <Zap className="text-blue-600" size={18} />
                <span className="text-sm font-semibold text-blue-600">Our Mission</span>
              </div>
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900">
                Empowering Businesses Through
                <span className="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent"> Innovation</span>
              </h2>
              <p className="text-lg text-gray-600 leading-relaxed">
                We provide cutting-edge IT consulting and software solutions tailored to drive innovation, 
                streamline operations, and fuel long-term success. Our commitment is to transform challenges 
                into opportunities through technology-driven strategies.
              </p>
              <div className="flex flex-wrap gap-4">
                <div className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-blue-50 to-purple-50 rounded-full">
                  <TrendingUp className="text-blue-600" size={18} />
                  <span className="text-sm font-semibold text-gray-700">Scalable Solutions</span>
                </div>
                <div className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-purple-50 to-pink-50 rounded-full">
                  <Award className="text-purple-600" size={18} />
                  <span className="text-sm font-semibold text-gray-700">Quality Assured</span>
                </div>
              </div>
            </div>
            <div className="flex items-center justify-center">
              <div className="relative w-full max-w-xs">
                <div className="absolute inset-0 bg-gradient-to-br from-blue-500 to-purple-600 rounded-3xl transform rotate-6" />
                <div className="relative bg-white rounded-3xl p-8 shadow-2xl transform -rotate-3">
                  <div className="space-y-4">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-xl flex items-center justify-center">
                        <Check className="text-white" size={20} strokeWidth={3} />
                      </div>
                      <div>
                        <div className="font-bold text-gray-900">Innovation</div>
                        <div className="text-sm text-gray-600">First approach</div>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-pink-500 rounded-xl flex items-center justify-center">
                        <Check className="text-white" size={20} strokeWidth={3} />
                      </div>
                      <div>
                        <div className="font-bold text-gray-900">Excellence</div>
                        <div className="text-sm text-gray-600">In delivery</div>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 bg-gradient-to-br from-orange-500 to-red-500 rounded-xl flex items-center justify-center">
                        <Check className="text-white" size={20} strokeWidth={3} />
                      </div>
                      <div>
                        <div className="font-bold text-gray-900">Partnership</div>
                        <div className="text-sm text-gray-600">Long-term</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Our Story Section */}
      <section className="py-24 px-4 lg:px-8 bg-gradient-to-br from-blue-50 to-purple-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Our <span className="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">Journey</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              From humble beginnings to industry leaders, here's how we've grown
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300">
              <div className="text-center">
                <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Globe className="text-white" size={32} />
                </div>
                <h3 className="text-3xl font-bold text-blue-600 mb-2">2020</h3>
                <p className="text-gray-600">Company Founded</p>
              </div>
            </div>

            <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300">
              <div className="text-center">
                <div className="w-16 h-16 bg-gradient-to-br from-purple-500 to-pink-500 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Users className="text-white" size={32} />
                </div>
                <h3 className="text-3xl font-bold text-purple-600 mb-2">{companyInfo.teamSize}</h3>
                <p className="text-gray-600">Team Members</p>
              </div>
            </div>

            <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300">
              <div className="text-center">
                <div className="w-16 h-16 bg-gradient-to-br from-orange-500 to-red-500 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Award className="text-white" size={32} />
                </div>
                <h3 className="text-3xl font-bold text-orange-600 mb-2">{companyInfo.projects}</h3>
                <p className="text-gray-600">Projects Completed</p>
              </div>
            </div>

            <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300">
              <div className="text-center">
                <div className="w-16 h-16 bg-gradient-to-br from-green-500 to-emerald-500 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Heart className="text-white" size={32} />
                </div>
                <h3 className="text-3xl font-bold text-green-600 mb-2">{companyInfo.clients}</h3>
                <p className="text-gray-600">Happy Clients</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Team Culture Section */}
      <section className="py-24 px-4 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div className="space-y-6">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-purple-50 rounded-full">
                <Heart className="text-purple-600" size={18} />
                <span className="text-sm font-semibold text-purple-600">Our Culture</span>
              </div>
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 leading-tight">
                Where Talent Meets
                <span className="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent"> Opportunity</span>
              </h2>
              <p className="text-lg text-gray-600 leading-relaxed">
                We foster a culture of continuous learning, innovation, and collaboration. Our team members are not just employees—they're partners in our mission to deliver excellence.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="flex items-start gap-4 p-4 bg-gradient-to-br from-blue-50 to-cyan-50 rounded-xl">
                  <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Shield className="text-white" size={20} />
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-1">Work-Life Balance</h4>
                    <p className="text-sm text-gray-600">Flexible schedules</p>
                  </div>
                </div>
                <div className="flex items-start gap-4 p-4 bg-gradient-to-br from-purple-50 to-pink-50 rounded-xl">
                  <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-pink-500 rounded-lg flex items-center justify-center flex-shrink-0">
                    <TrendingUp className="text-white" size={20} />
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-1">Growth Focus</h4>
                    <p className="text-sm text-gray-600">Career development</p>
                  </div>
                </div>
                <div className="flex items-start gap-4 p-4 bg-gradient-to-br from-orange-50 to-red-50 rounded-xl">
                  <div className="w-10 h-10 bg-gradient-to-br from-orange-500 to-red-500 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Users className="text-white" size={20} />
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-1">Collaborative</h4>
                    <p className="text-sm text-gray-600">Team-first approach</p>
                  </div>
                </div>
                <div className="flex items-start gap-4 p-4 bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl">
                  <div className="w-10 h-10 bg-gradient-to-br from-green-500 to-emerald-500 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Clock className="text-white" size={20} />
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-1">Innovative</h4>
                    <p className="text-sm text-gray-600">Latest technologies</p>
                  </div>
                </div>
              </div>
            </div>
            <div className="relative">
              <div className="absolute -inset-4 bg-gradient-to-r from-purple-500 to-pink-600 rounded-3xl opacity-20 blur-2xl" />
              <img
                src="https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=800&q=85"
                alt="Team Culture"
                className="relative rounded-3xl shadow-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 px-4 lg:px-8 bg-gradient-to-br from-purple-600 via-pink-600 to-orange-600 relative overflow-hidden">
        <div className="absolute inset-0 bg-grid-white/10" />
        <div className="max-w-4xl mx-auto text-center relative z-10 space-y-8">
          <h2 className="text-4xl md:text-5xl font-bold text-white leading-tight">
            Ready to Transform Your Business?
          </h2>
          <p className="text-xl text-white/90">
            Let's discuss how we can help you achieve your digital transformation goals
          </p>
          <button
            onClick={() => onCtaClick && onCtaClick()}
            className="inline-flex items-center gap-2 bg-white text-purple-600 px-8 py-4 rounded-xl text-base font-semibold shadow-xl hover:shadow-2xl transform hover:-translate-y-1 transition-all duration-300"
          >
            Contact Us Today
            <ArrowRight size={20} />
          </button>
        </div>
      </section>
    </div>
  );
};

export default About;